'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { redeemPendingClaim } from '@/lib/shared-link-claim';

/**
 * Redeems an anonymous short link into the workspace after sign-in.
 *
 * Mounted in the authenticated layout rather than on a single auth page,
 * because the claim has to survive every route into the dashboard. It used to
 * run only on /auth/verify-email — a page reachable only by registering — so
 * anyone whose e-mail already had an account could never claim the link they
 * had just created on the website. Here it covers registration and ordinary
 * sign-in alike.
 *
 * redeemPendingClaim never throws and spends the token before the request
 * fires, so this cannot block rendering or claim twice.
 */
export function SharedLinkClaimRedeemer() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // React strict mode mounts effects twice in dev.
    ran.current = true;

    void redeemPendingClaim().then((outcome) => {
      if (outcome.status === 'claimed') {
        // The dashboard may have loaded before the claim landed; without this it
        // showed a brand-new user "0 mappings" next to the toast.
        void queryClient.invalidateQueries({ queryKey: ['mappings'] });
        toast.success('Your short link was added to your workspace', {
          action: { label: 'View links', onClick: () => router.push('/links') },
        });
      } else if (outcome.status === 'failed') {
        toast.info(
          "We couldn't add your shared link — it may have expired or already been claimed. Your account is ready either way."
        );
      }
    });
  }, [router, queryClient]);

  return null;
}
